import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { apiGet } from '../utils/api';
import config from '../config';
import './VirtualTableExperiment.css';

/**
 * 임금대장 "실제 프로세스" 가상화 실험 컴포넌트 (성능 최적화판).
 *
 * 실제 백엔드 Excel-read 응답
 *   GET /api/salary/companies/{id}/salary-data/{year}/{month}/version
 * 을 그대로 받아 실제 다단계 헤더로 렌더하면서, "보이는 행만 렌더링(가상화)"의
 * 체감 성능을 검증한다. 프로덕션 Salary.js는 건드리지 않는다.
 *
 * 스크롤 지연(값 pop-in) 최소화를 위한 최적화:
 *  1) 포맷된 셀 값/클래스/스타일을 로드 시 1회 미리 계산 → 스크롤 경로에서 제거
 *  2) 각 행을 React.memo → 윈도우 겹치는 행은 재렌더 스킵
 *  3) 오버스캔 확대 → 빠른 스크롤에도 행이 미리 준비됨
 *  4) 스크롤 시 윈도우(행 구간)가 실제로 바뀔 때만 setState
 */

const FIELD_DELIMITER = config.FIELD_DELIMITER;
const NAME_FIELD = config.CONTRACT_BASIC_NAME;
const ID_FIELD = config.CONTRACT_BASIC_ID;
const JOIN_FIELD = ['기초', '인적정보', '입사일', 'nan'].join(FIELD_DELIMITER);
const LEAVE_FIELD = ['기초', '인적정보', '퇴사일', 'nan'].join(FIELD_DELIMITER);

const ROW_H = 30;
const OVERSCAN = 14;
const ROW_STYLE = { height: ROW_H };
const DEFAULTS = { companyId: '6a56e457185579af8558c8ad', year: 2026, month: 1 };
const nf = new Intl.NumberFormat('ko-KR');

function formatHeader(header) {
  if (!header) return [];
  return String(header).split(FIELD_DELIMITER)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && p.toLowerCase() !== 'nan');
}

function formatCellValue(value) {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  const dateTime = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/;
  const isoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
  if (dateTime.test(s) || isoDateTime.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
  }
  if (dateOnly.test(s)) return s;
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (!Number.isNaN(n)) {
      const r = Math.round(n);
      if (r === 0) return '';
      return r.toLocaleString('ko-KR', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    }
  }
  return s;
}

const isNumericStr = (v) => v !== null && v !== undefined && String(v).trim() !== '' && /^-?\d+(\.\d+)?$/.test(String(v).trim());
const isEmptyOrZero = (v) => {
  if (v === null || v === undefined || v === '') return true;
  const s = String(v).trim();
  if (s === '') return true;
  const n = parseFloat(s);
  return !Number.isNaN(n) && n === 0;
};
const ymOf = (s) => {
  const m = String(s || '').match(/(\d{4})[-/.](\d{1,2})/);
  return m ? { y: +m[1], mo: +m[2] } : null;
};

/** 미리 계산된 행 메타(meta)와 안정적 컬럼 서술(cols)만 받는 순수 행 → memo로 재렌더 최소화 */
const Row = React.memo(function Row({ meta, cols }) {
  const rowCls = meta.joined ? 'joined' : (meta.left ? 'left' : meta.base);
  return (
    <tr className={rowCls} style={ROW_STYLE}>
      {cols.map((c) => (
        <td key={c.key} className={meta.negs[c.key] ? `${c.cls} neg` : c.cls} style={c.style}>
          {meta.vals[c.key]}
          {c.isName && meta.joined && <span className="vtx-tag j">입사</span>}
          {c.isName && meta.left && <span className="vtx-tag l">퇴사</span>}
        </td>
      ))}
    </tr>
  );
});

export default function VirtualTableExperiment() {
  const [companyId, setCompanyId] = useState(DEFAULTS.companyId);
  const [year, setYear] = useState(DEFAULTS.year);
  const [month, setMonth] = useState(DEFAULTS.month);

  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadMs, setLoadMs] = useState(null);

  const [virtualized, setVirtualized] = useState(true);
  const [range, setRange] = useState({ start: 0, end: 0 });
  const [fps, setFps] = useState(null);

  const scrollRef = useRef(null);
  const scrollTopRef = useRef(0);
  const viewportHRef = useRef(480);
  const lastScrollTs = useRef(0);

  const loadData = useCallback(async (cid, y, m) => {
    if (!cid) { setError('회사 ID를 입력하세요.'); return; }
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const url = `/api/salary/companies/${cid}/salary-data/${y}/${m}/version`;
      const data = await apiGet(url);
      setSalaryData(data);
      setLoadMs(Math.round(performance.now() - t0));
      scrollTopRef.current = 0;
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch (e) {
      console.error('[VirtualTableExperiment] load failed:', e);
      setError(e?.message || '데이터를 불러오지 못했습니다. (로그인/회사 ID/해당 월 데이터 확인)');
      setSalaryData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(DEFAULTS.companyId, DEFAULTS.year, DEFAULTS.month); }, [loadData]);

  const headers = useMemo(() => (salaryData?.headers || []), [salaryData]);
  const data = useMemo(() => (salaryData?.data || []), [salaryData]);

  const visibleColumns = useMemo(() => {
    if (!headers.length) return [];
    return headers.filter((h) => {
      if (h === NAME_FIELD || h === ID_FIELD) return true;
      return !data.every((r) => isEmptyOrZero(r[h]));
    });
  }, [headers, data]);

  const numericSet = useMemo(() => {
    const set = new Set();
    const sampleN = Math.min(data.length, 200);
    const step = Math.max(1, Math.floor(data.length / Math.max(1, sampleN)));
    visibleColumns.forEach((h) => {
      let num = 0; let nonEmpty = 0;
      for (let i = 0; i < data.length; i += step) {
        const v = data[i][h];
        if (v === null || v === undefined || String(v).trim() === '') continue;
        nonEmpty += 1;
        if (isNumericStr(v)) num += 1;
      }
      if (nonEmpty > 0 && num / nonEmpty >= 0.6) set.add(h);
    });
    return set;
  }, [visibleColumns, data]);

  const widths = useMemo(() => {
    const out = {};
    if (!visibleColumns.length) return out;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", "Malgun Gothic", sans-serif';
    const sampleN = Math.min(data.length, 120);
    const step = Math.max(1, Math.floor(data.length / Math.max(1, sampleN)));
    visibleColumns.forEach((h) => {
      let max = 0;
      formatHeader(h).forEach((line) => { max = Math.max(max, ctx.measureText(line).width); });
      for (let i = 0; i < data.length; i += step) {
        const v = formatCellValue(data[i][h]);
        if (v) max = Math.max(max, ctx.measureText(String(v)).width);
      }
      out[h] = Math.min(260, Math.max(60, Math.ceil(max) + 22));
    });
    return out;
  }, [visibleColumns, data]);

  const { fixedLeft, lastFixedKey } = useMemo(() => {
    let acc = 0; const map = {}; let last = null;
    visibleColumns.forEach((h) => {
      if (h === NAME_FIELD || h === ID_FIELD) { map[h] = acc; acc += (widths[h] || 100); last = h; }
    });
    return { fixedLeft: map, lastFixedKey: last };
  }, [visibleColumns, widths]);

  // 안정적 컬럼 서술(스크롤 중 참조 불변) — 클래스/스타일/헤더텍스트 미리 계산
  const cols = useMemo(() => visibleColumns.map((h) => {
    const isFixed = (h === NAME_FIELD || h === ID_FIELD);
    const isNum = numericSet.has(h);
    const cls = [isNum ? 'num' : '', isFixed ? 'fix' : '', h === lastFixedKey ? 'last' : ''].filter(Boolean).join(' ');
    const parts = formatHeader(h);
    return {
      key: h, isName: h === NAME_FIELD, isNum, isFixed, cls,
      style: isFixed ? { left: fixedLeft[h] } : undefined,
      headerText: parts.join('\n'), headerTitle: parts.join(' '),
    };
  }), [visibleColumns, numericSet, lastFixedKey, fixedLeft]);

  const isJoined = useCallback((r) => { const d = ymOf(r?.[JOIN_FIELD]); return !!d && d.y === Number(year) && d.mo === Number(month); }, [year, month]);
  const isLeft = useCallback((r) => { const d = ymOf(r?.[LEAVE_FIELD]); return !!d && d.y === Number(year) && d.mo === Number(month); }, [year, month]);

  // 행 메타 미리 계산: 포맷된 값 + 음수/입퇴사 플래그. 스크롤 경로에서 포맷 연산 제거.
  const rowsMeta = useMemo(() => data.map((row, idx) => {
    const vals = {}; const negs = {};
    for (let c = 0; c < cols.length; c += 1) {
      const col = cols[c];
      const raw = row[col.key];
      vals[col.key] = formatCellValue(raw);
      if (col.isNum && isNumericStr(raw) && parseFloat(String(raw)) < 0) negs[col.key] = true;
    }
    return { idx, base: idx % 2 ? 'odd' : 'even', joined: isJoined(row), left: isLeft(row), vals, negs };
  }), [data, cols, isJoined, isLeft]);

  const totals = useMemo(() => {
    const t = {};
    cols.forEach((c) => {
      if (!c.isNum) return;
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = data[i][c.key];
        if (isNumericStr(v)) sum += Math.round(parseFloat(String(v).trim()));
      }
      t[c.key] = sum;
    });
    return t;
  }, [cols, data]);

  const total = data.length;

  // 윈도우 계산: 실제로 start/end가 바뀔 때만 setState → 불필요한 리렌더 억제
  const recalc = useCallback(() => {
    let start = 0; let end = total;
    if (virtualized) {
      const st = scrollTopRef.current;
      const vh = viewportHRef.current;
      start = Math.max(0, Math.floor(st / ROW_H) - OVERSCAN);
      end = Math.min(total, Math.ceil((st + vh) / ROW_H) + OVERSCAN);
    }
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, [virtualized, total]);

  useEffect(() => { recalc(); }, [recalc]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => { viewportHRef.current = el.clientHeight; recalc(); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalc]);

  const onScroll = useCallback((e) => {
    lastScrollTs.current = performance.now();
    scrollTopRef.current = e.currentTarget.scrollTop;
    recalc();
  }, [recalc]);

  // FPS(스크롤 중에만)
  useEffect(() => {
    let frames = 0; let last = performance.now(); let raf; let shown = false;
    const loop = (now) => {
      frames += 1;
      if (now - last >= 500) {
        const value = Math.round((frames * 1000) / (now - last));
        frames = 0; last = now;
        const scrolling = now - lastScrollTs.current < 700;
        if (scrolling) { setFps(value); shown = true; }
        else if (shown) { setFps(null); shown = false; }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const start = Math.min(range.start, total);
  const end = Math.min(range.end, total);
  const topPad = start * ROW_H;
  const bottomPad = Math.max(0, (total - end) * ROW_H);
  const mounted = Math.max(0, end - start);
  const visibleMeta = rowsMeta.slice(start, end);

  return (
    <div className="vtx">
      <div className="vtx-head">
        <div className="vtx-title-wrap">
          <span className="vtx-eyebrow">Rendering Experiment · 실제 API · Salary.js 무변경</span>
          <h3 className="vtx-title">임금대장 실제-데이터 가상화 렌더링 실험</h3>
          <p className="vtx-sub">
            실제 백엔드 Excel-read 응답을 그대로 받아 실제 다단계 헤더로 렌더합니다.
            <b> 가상화 ON</b>이면 보이는 행만 DOM에 올리고, <b>OFF</b>면 전체 행을 올립니다(현재 프로덕션과 동일).
            빠르게 스크롤하며 FPS와 “마운트된 행”을 비교하세요. <i>(로그인된 상태에서만 데이터가 로드됩니다.)</i>
          </p>
        </div>

        <div className="vtx-loader">
          <label className="vtx-field">
            <span>회사 ID</span>
            <input value={companyId} onChange={(e) => setCompanyId(e.target.value.trim())} spellCheck={false} />
          </label>
          <label className="vtx-field sm">
            <span>년</span>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
          </label>
          <label className="vtx-field sm">
            <span>월</span>
            <input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
          <button type="button" className="vtx-btn" disabled={loading} onClick={() => loadData(companyId, year, month)}>
            {loading ? '불러오는 중…' : '불러오기'}
          </button>
          {loadMs != null && !loading && <span className="vtx-load-ms">로드 {loadMs}ms · {total}행 · {visibleColumns.length}열</span>}
        </div>

        <div className="vtx-controls">
          <label className="vtx-toggle" title="켜면 보이는 행만 렌더링합니다">
            <input type="checkbox" checked={virtualized} onChange={(e) => setVirtualized(e.target.checked)} />
            <span className="vtx-track" />
            <span className="vtx-toggle-text">
              <span className="vtx-toggle-lbl">가상화</span>
              <span className="vtx-toggle-state">{virtualized ? 'ON — 보이는 행만 렌더' : 'OFF — 전체 행 렌더'}</span>
            </span>
          </label>

          <div className="vtx-metrics">
            <div className="vtx-metric"><span className="k">총 행수</span><span className="v">{total}</span></div>
            <div className="vtx-metric">
              <span className="k">마운트된 행</span>
              <span className={`v ${mounted > 120 ? 'heavy' : ''}`}>{mounted}<small> / {total}</small></span>
            </div>
            <div className="vtx-metric">
              <span className="k">스크롤 FPS</span>
              <span className={`v ${fps == null ? '' : (fps >= 50 ? 'good' : (fps < 35 ? 'bad' : ''))}`}>{fps == null ? '–' : fps}</span>
            </div>
          </div>

          <div className="vtx-legend">
            <span><i className="sw joined" /> {month}월 입사</span>
            <span><i className="sw left" /> {month}월 퇴사</span>
          </div>
        </div>
      </div>

      {error && <div className="vtx-status err">{error}</div>}
      {!error && !loading && total === 0 && <div className="vtx-status">해당 회사/연월의 임금대장 데이터가 없습니다.</div>}

      {total > 0 && (
        <div className="vtx-scroll" ref={scrollRef} onScroll={onScroll}>
          <table className="vtx-table">
            <colgroup>
              {cols.map((c) => <col key={c.key} style={{ width: widths[c.key] }} />)}
            </colgroup>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className={c.cls} style={c.style} title={c.headerTitle}>{c.headerText}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topPad > 0 && (
                <tr className="vtx-spacer" style={{ height: topPad }}><td colSpan={cols.length} /></tr>
              )}
              {visibleMeta.map((meta) => <Row key={meta.idx} meta={meta} cols={cols} />)}
              {bottomPad > 0 && (
                <tr className="vtx-spacer" style={{ height: bottomPad }}><td colSpan={cols.length} /></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                {cols.map((c) => (
                  <td key={c.key} className={c.isName ? `${c.cls} label` : c.cls} style={c.style}>
                    {c.isName ? `합계 (${total}명)` : (c.isNum ? nf.format(totals[c.key] || 0) : '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="vtx-notes">
        <div className="vtx-note">
          <h4>이 실험이 실제와 같은 점</h4>
          <ul>
            <li>동일 API <code>/api/salary/.../salary-data/{'{y}'}/{'{m}'}/version</code> 호출(실제 Excel-read 응답)</li>
            <li>실제 다단계 헤더·빈 컬럼 자동 숨김·<code>formatCellValue</code> 반올림 규칙 그대로</li>
            <li>성명·주민번호 고정열, 헤더 sticky, 합계행 sticky</li>
          </ul>
        </div>
        <div className="vtx-note">
          <h4>스크롤 지연을 줄인 방법</h4>
          <ul>
            <li>포맷된 값·클래스·스타일을 <b>로드 시 1회 미리 계산</b>(스크롤 경로에서 정규식/날짜 파싱 제거)</li>
            <li>행을 <code>React.memo</code>로 감싸 유지되는 행은 재렌더 스킵 + 오버스캔 {OVERSCAN}행</li>
            <li>스크롤 시 행 구간이 실제로 바뀔 때만 리렌더</li>
          </ul>
        </div>
        <div className="vtx-note">
          <h4>남는 지연이 있다면</h4>
          <p>
            열이 매우 많으면(수십 열) 한 행의 셀 수가 커서 여전히 무거울 수 있습니다. 그 경우
            <b> 열 가상화</b>(보이는 열만 렌더)나 오버스캔 추가 확대로 더 줄일 수 있습니다. 필요하면 이어서 적용하겠습니다.
          </p>
        </div>
      </div>
    </div>
  );
}
